import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, TrendingUp } from "lucide-react";
import Stat from "./optimize/Stat";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Advantage+ Shopping (ASC) — candidate list, auto-promote toggle, audit history.
 */
export default function ASCPanel({ isAdmin }) {
  const [data, setData] = useState({ candidates: [], count: 0, threshold_conv_7d: 50 });
  const [roiData, setRoiData] = useState({ candidates: [], count: 0, min_roi_pct: 40, days_required: 3, min_daily_spend: 20 });
  const [history, setHistory] = useState([]);
  const [auto, setAuto] = useState({
    enabled: false, promote_live: false, daily_budget_usd: 50,
    roi_trigger: { enabled: false, min_roi_pct: 40, days_required: 3, min_daily_spend: 20 },
  });
  const [busy, setBusy] = useState({ load: false, save: false, promote: null, roiPromote: null, roiLoad: false });

  const load = async () => {
    setBusy((b) => ({ ...b, load: true }));
    try {
      const [c, h, a] = await Promise.all([
        api.get("/optimize/asc-candidates"),
        api.get("/optimize/asc-history"),
        api.get("/optimize/asc-auto"),
      ]);
      setData(c.data);
      setHistory(h.data.promotions || []);
      const cfg = a.data;
      setAuto({
        enabled: cfg.enabled, promote_live: cfg.promote_live,
        daily_budget_usd: cfg.daily_budget_usd,
        roi_trigger: cfg.roi_trigger || { enabled: false, min_roi_pct: 40, days_required: 3, min_daily_spend: 20 },
      });
      // preview the ROI candidates with the saved trigger params
      const rt = cfg.roi_trigger || {};
      try {
        const { data: rr } = await api.get("/optimize/asc-roi-candidates", {
          params: {
            min_roi_pct: rt.min_roi_pct ?? 40,
            days_required: rt.days_required ?? 3,
            min_daily_spend: rt.min_daily_spend ?? 20,
          },
        });
        setRoiData(rr);
      } catch (e) { console.error("roi preview:", e); }
    } finally {
      setBusy((b) => ({ ...b, load: false }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const refreshRoi = async () => {
    setBusy((b) => ({ ...b, roiLoad: true }));
    try {
      const rt = auto.roi_trigger;
      const { data: rr } = await api.get("/optimize/asc-roi-candidates", {
        params: { min_roi_pct: rt.min_roi_pct, days_required: rt.days_required, min_daily_spend: rt.min_daily_spend },
      });
      setRoiData(rr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy((b) => ({ ...b, roiLoad: false }));
    }
  };

  const promoteRoi = async (row, live = false) => {
    setBusy((b) => ({ ...b, roiPromote: row.campaign_id }));
    try {
      const rt = auto.roi_trigger;
      const { data: res } = await api.post("/optimize/asc-roi-promote", {
        campaign_id: row.campaign_id,
        daily_budget_usd: auto.daily_budget_usd,
        dry_run: !live,
        min_roi_pct: rt.min_roi_pct,
        days_required: rt.days_required,
        min_daily_spend: rt.min_daily_spend,
      });
      if (res.status === "created_paused") {
        toast.success(`ASC #${res.asc_campaign_id} created paused (ROI ${row.avg_roi_pct}%).`);
      } else if (res.status === "planned") {
        toast.success(`Dry-run planned for campaign ${row.campaign_id}`);
      } else if (res.status === "skipped_no_fb_connection") {
        toast.error("FB not connected — connect your token first");
      } else {
        toast.error(`ASC ROI-promote ${res.status}: ${res.error || ""}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setBusy((b) => ({ ...b, roiPromote: null }));
    }
  };

  const promote = async (row, live = false) => {
    setBusy((b) => ({ ...b, promote: row.ad_id }));
    try {
      const { data: res } = await api.post("/optimize/asc-promote", {
        ad_id: row.ad_id,
        daily_budget_usd: auto.daily_budget_usd,
        dry_run: !live,
      });
      if (res.status === "created_paused") {
        toast.success(`ASC #${res.asc_campaign_id} created paused. Review in Ads Manager.`);
      } else if (res.status === "planned") {
        toast.success(`Dry-run planned for ad ${row.ad_id} (no live FB call)`);
      } else if (res.status === "skipped_no_fb_connection") {
        toast.error("FB not connected — connect your token first");
      } else {
        toast.error(`ASC promote ${res.status}: ${res.error || ""}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setBusy((b) => ({ ...b, promote: null }));
    }
  };

  const saveAuto = async () => {
    setBusy((b) => ({ ...b, save: true }));
    try {
      const { data: res } = await api.post("/optimize/asc-auto", auto);
      setAuto(res);
      toast.success(res.enabled ? `Auto ASC ${res.promote_live ? "(LIVE)" : "(dry-run)"} enabled` : "Auto ASC paused");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, save: false }));
    }
  };

  const statusTag = (st) => {
    if (st === "created_paused") return "text-emerald-700 bg-emerald-50 border-emerald-200";
    if (st === "planned")        return "text-blue-700 bg-blue-50 border-blue-200";
    if (st === "error")          return "text-red-700 bg-red-50 border-red-200";
    return "text-amber-700 bg-amber-50 border-amber-200";
  };

  return (
    <div className="space-y-4" data-testid="asc-panel">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Threshold" value={`${data.threshold_conv_7d} conv / 7d`} color="slate" sub="ASC minimum" />
        <Stat label="Candidates" value={data.count} color="emerald" sub="winners ready" />
        <Stat label="Promoted" value={history.length} color="blue" sub="lifetime" />
      </div>

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="asc-auto-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                Nightly auto-promote
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${auto.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                  {auto.enabled ? (auto.promote_live ? "LIVE" : "dry-run") : "off"}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 font-mono mt-1">
                Runs at 04:00 UTC. Any ad that crossed 50 conv/7d gets a new paused ASC campaign.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer">
              <input type="checkbox" checked={auto.enabled} onChange={(e) => setAuto({ ...auto, enabled: e.target.checked })} data-testid="asc-auto-enabled" />
              Enable nightly auto-ASC
            </label>
            <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${!auto.enabled ? "opacity-50" : ""}`}>
              <input type="checkbox" checked={auto.promote_live} onChange={(e) => setAuto({ ...auto, promote_live: e.target.checked })} disabled={!auto.enabled} data-testid="asc-auto-live" />
              Promote LIVE (calls FB Marketing API)
            </label>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[10px] font-mono text-slate-500">seed budget</span>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-mono">$</span>
                <input
                  type="number" min={5} max={500} step={5}
                  value={auto.daily_budget_usd}
                  onChange={(e) => setAuto({ ...auto, daily_budget_usd: parseFloat(e.target.value) || 50 })}
                  data-testid="asc-auto-budget"
                  className="w-20 bg-white border border-slate-200 rounded px-4 py-1 text-xs font-mono text-right"
                />
              </div>
              <span className="text-[10px] font-mono text-slate-500">/ day</span>
            </div>
            <button onClick={saveAuto} disabled={busy.save} data-testid="asc-auto-save" className="bg-blue-600 text-white rounded-md px-3 py-1 text-xs font-medium hover:bg-blue-700 disabled:opacity-60">
              {busy.save ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="bg-white border border-amber-200 rounded-md p-4 space-y-3" data-testid="asc-roi-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                <span>Scale by ROI</span>
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${auto.roi_trigger?.enabled ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                  {auto.roi_trigger?.enabled ? "armed" : "off"}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 font-mono mt-1">
                Auto-promote any campaign whose ROI ≥ threshold every day for N consecutive days.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={!!auto.roi_trigger?.enabled}
                onChange={(e) => setAuto({ ...auto, roi_trigger: { ...auto.roi_trigger, enabled: e.target.checked } })}
                data-testid="asc-roi-enabled"
              />
              Enable ROI scaler
            </label>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-slate-500">min ROI</span>
              <input
                type="number" step={5} min={0} max={500}
                value={auto.roi_trigger?.min_roi_pct ?? 40}
                onChange={(e) => setAuto({ ...auto, roi_trigger: { ...auto.roi_trigger, min_roi_pct: parseFloat(e.target.value) || 0 } })}
                data-testid="asc-roi-min-pct"
                className="w-16 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono text-right"
              />
              <span className="text-[10px] font-mono text-slate-500">%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-slate-500">for</span>
              <input
                type="number" min={1} max={14} step={1}
                value={auto.roi_trigger?.days_required ?? 3}
                onChange={(e) => setAuto({ ...auto, roi_trigger: { ...auto.roi_trigger, days_required: parseInt(e.target.value) || 3 } })}
                data-testid="asc-roi-days"
                className="w-12 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono text-right"
              />
              <span className="text-[10px] font-mono text-slate-500">consecutive days</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-slate-500">min daily spend</span>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-mono">$</span>
                <input
                  type="number" min={0} step={5}
                  value={auto.roi_trigger?.min_daily_spend ?? 20}
                  onChange={(e) => setAuto({ ...auto, roi_trigger: { ...auto.roi_trigger, min_daily_spend: parseFloat(e.target.value) || 0 } })}
                  data-testid="asc-roi-min-spend"
                  className="w-20 bg-white border border-slate-200 rounded px-4 py-1 text-xs font-mono text-right"
                />
              </div>
            </div>
            <button onClick={refreshRoi} disabled={busy.roiLoad} data-testid="asc-roi-preview" className="bg-white border border-slate-200 hover:bg-slate-100 rounded px-2 py-1 text-[11px] font-medium">
              {busy.roiLoad ? "…" : "Preview"}
            </button>
          </div>
          <div className="text-[11px] font-mono text-slate-500">
            Current preview: <strong className="text-slate-900">{roiData.count}</strong> campaign{roiData.count === 1 ? "" : "s"} qualifying
            at ROI ≥ {auto.roi_trigger?.min_roi_pct ?? 40}% × {auto.roi_trigger?.days_required ?? 3}d × min ${auto.roi_trigger?.min_daily_spend ?? 20}/day spend
            {auto.enabled && auto.roi_trigger?.enabled && (
              <span className="ml-2 inline-block bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
                will auto-promote {auto.promote_live ? "LIVE" : "as dry-run"} tonight @ 04:00 UTC
              </span>
            )}
          </div>
          {roiData.count > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-slate-100" data-testid="asc-roi-candidates">
              {roiData.candidates.slice(0, 5).map((row) => (
                <div key={row.campaign_id} className="flex items-center gap-3 bg-amber-50/50 border border-amber-200 rounded px-3 py-2 text-xs" data-testid={`asc-roi-candidate-${row.campaign_id}`}>
                  <TrendingUp className="w-4 h-4 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-slate-800 truncate">{row.campaign_name || row.campaign_id}</div>
                    <div className="text-[10px] font-mono text-slate-500">
                      avg ROI {row.avg_roi_pct}% · min {row.min_roi_pct}% × {row.days_qualified}d · {fmtMoney(row.total_spend)} spend → {fmtMoney(row.total_revenue)} revenue
                    </div>
                  </div>
                  <button onClick={() => promoteRoi(row, false)} disabled={busy.roiPromote === row.campaign_id} data-testid={`asc-roi-promote-dry-${row.campaign_id}`} className="bg-white border border-slate-200 hover:bg-slate-100 rounded px-2 py-1 text-[11px] font-medium">Dry-run</button>
                  <button onClick={() => promoteRoi(row, true)} disabled={busy.roiPromote === row.campaign_id} data-testid={`asc-roi-promote-live-${row.campaign_id}`} className="bg-amber-600 text-white hover:bg-amber-700 rounded px-2 py-1 text-[11px] font-medium disabled:opacity-60">
                    {busy.roiPromote === row.campaign_id ? "…" : "Promote LIVE"}
                  </button>
                </div>
              ))}
              {roiData.count > 5 && (
                <div className="text-[10px] font-mono text-slate-400 italic">
                  + {roiData.count - 5} more qualifying campaign{roiData.count - 5 === 1 ? "" : "s"} will auto-promote tonight.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="asc-candidates">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Candidates ({data.count})</div>
          <button onClick={load} disabled={busy.load} data-testid="asc-refresh" className="text-[10px] font-mono text-slate-500 hover:text-slate-900 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> refresh
          </button>
        </div>
        {data.candidates.length === 0 ? (
          <div className="text-xs font-mono text-slate-400 italic py-6 text-center" data-testid="asc-candidates-empty">
            No ads have crossed {data.threshold_conv_7d} conversions in the last 7 days yet. Keep scaling your winners.
          </div>
        ) : (
          <div className="space-y-2">
            {data.candidates.map((row) => (
              <div key={row.ad_id} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs" data-testid={`asc-candidate-${row.ad_id}`}>
                <TrendingUp className="w-4 h-4 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-slate-800 truncate">{row.campaign_name || row.ad_id}</div>
                  <div className="text-[10px] font-mono text-slate-500">{row.conversions_7d} conv · {fmtMoney(row.revenue_7d)} / 7d</div>
                </div>
                <button onClick={() => promote(row, false)} disabled={busy.promote === row.ad_id} data-testid={`asc-promote-dry-${row.ad_id}`} className="bg-white border border-slate-200 hover:bg-slate-100 rounded px-2 py-1 text-[11px] font-medium">Dry-run</button>
                <button onClick={() => promote(row, true)} disabled={busy.promote === row.ad_id} data-testid={`asc-promote-live-${row.ad_id}`} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded px-2 py-1 text-[11px] font-medium disabled:opacity-60">
                  {busy.promote === row.ad_id ? "…" : "Promote LIVE"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="asc-history">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Recent ASC activity</div>
        {history.length === 0 ? (
          <div className="text-xs font-mono text-slate-400 italic py-4 text-center">No promotions yet.</div>
        ) : (
          <div className="space-y-1">
            {history.slice(0, 10).map((h) => (
              <div key={h.promotion_id} className="flex items-center gap-2 text-[11px] font-mono" data-testid={`asc-history-${h.promotion_id}`}>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusTag(h.status)}`}>{h.status}</span>
                <span className="text-slate-500">{new Date(h.at).toLocaleString()}</span>
                <span className="text-slate-700 truncate" title={h.source_ad_id}>ad {h.source_ad_id}</span>
                <span className="text-slate-500 ml-auto">${h.daily_budget_usd}/day</span>
                {h.asc_campaign_id && <span className="text-emerald-600">→ {h.asc_campaign_id}</span>}
                {h.dry_run && <span className="text-[9px] uppercase bg-blue-100 text-blue-700 border border-blue-200 rounded px-1">dry</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
